import React, { useState } from 'react'
import {
  Grid,
  Typography,
  Box,
  IconButton,
  Chip,
  Paper,
  CircularProgress
} from '@mui/material'
import {
  // Add as AddIcon,
  Delete as DeleteIcon,
  Home as HomeIcon,
  Description as PageIcon,
  Link as LinkIcon
} from '@mui/icons-material'
import { ActionButton, ConfirmationAlert } from '@emerald-works-nova/components'
import { useStyles } from './style'
import CreatePageModal from '../modals/create-page-modal'

const PagesList = ({ pages, onPageCreate, onPageEdit, onPageDelete, isLoading }) => {
  const classes = useStyles()
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [pageToDelete, setPageToDelete] = useState(null)

  const handleCreatePage = (pageData) => {
    onPageCreate(pageData)
    setCreateModalOpen(false)
  }

  const handleDeleteClick = (page) => {
    setPageToDelete(page)
    setDeleteModalOpen(true)
  }

  const handleConfirmDelete = () => {
    if (pageToDelete) {
      onPageDelete(pageToDelete.id)
      setDeleteModalOpen(false)
      setPageToDelete(null)
    }
  }

  const handleCancelDelete = () => {
    setDeleteModalOpen(false)
    setPageToDelete(null)
  }

  /**
   * Formats a date string to a readable format
   * @param {string} dateString - ISO date string
   * @returns {string} Formatted date string
   */
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Box>
          <Typography variant='body2' color='textSecondary' className={classes.subtitle}>
            Create and manage your website pages. Configure sections, content, and layout for each page.
          </Typography>
        </Box>
        {/* <Box className={classes.headerActions}>
          <ActionButton
            variant='primary'
            startIcon={<AddIcon />}
            onClick={() => setCreateModalOpen(true)}
            dataTest='cms-create-page-button'
          >
            Create Page
          </ActionButton>
        </Box> */}
      </Box>

      <Paper
        className={classes.tablePaper}
        tabIndex={0}
        aria-label='Pages List'
        elevation={0}
        data-test='pages-table'
      >
        <Grid
          container
          tabIndex={0}
          direction='row'
          alignItems='center'
          className={classes.gridHeader}
          aria-label='List headers: Page, Sections, Modified, Status, Actions'
        >
          <Grid item md={5.5} className={classes.headerItemLeft}>Page</Grid>
          <Grid item md={1.5} className={classes.headerItemCenter}>Sections</Grid>
          <Grid item md={1.5}>Modified</Grid>
          <Grid item md={1.5}>Status</Grid>
          <Grid item md={2} className={classes.headerItemRight}>Actions</Grid>
        </Grid>

        {isLoading && (
          <Grid item xs={12} container justifyContent='center' alignItems='center' padding={2} gap={1} flexDirection='column'>
            <CircularProgress size={40} color='primary' />
            <Typography variant='body2' color='textSecondary'>
              Loading pages...
            </Typography>
          </Grid>
        )}

        {pages.map((page, index) => (
          <Grid
            key={index}
            role='row button'
            data-test={`page-list-item-${index}`}
            container
            tabIndex={0}
            direction='row'
            alignItems='flex-start'
            className={`${classes.gridRow} ${page.isDefault ? classes.defaultPageRow : ''}`}
          >
            <Grid item md={5.5} className={classes.pageNameColumn}>
              <Box display='flex' alignItems='flex-start'>
                <Box className={`${classes.pageIconWrapper} ${page.isDefault ? classes.defaultPageIconWrapper : classes.regularPageIconWrapper}`}>
                  {page.isDefault ? (
                    <HomeIcon className={`${classes.listPageIcon} ${classes.defaultPageIcon}`} />
                  ) : (
                    <PageIcon className={`${classes.listPageIcon} ${classes.regularPageIcon}`} />
                  )}
                </Box>
                <Box>
                  <Box display='flex' alignItems='center' gap={1}>
                    <Typography
                      variant='body2'
                      className={classes.pageTitle}
                      data-test={`page-title-${index}`}
                    >
                      {page.name}
                    </Typography>
                    {page.isDefault && (
                      <Chip
                        label='Default'
                        size='small'
                        className={classes.defaultChip}
                      />
                    )}
                  </Box>
                  <Typography variant='caption' color='textSecondary' className={classes.pageDescription}>
                    {page.description}
                  </Typography>
                  <Typography variant='caption' color='textSecondary' className={classes.pageUrl}>
                    <LinkIcon className={classes.linkIcon} />
                    <span className={classes.pathDot}>•</span>
                    {page.slug}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item md={1.5} className={classes.gridItemCenter}>
              <Typography variant='body2'>
                {page.sections?.length || 0}
              </Typography>
            </Grid>

            <Grid item md={1.5} className={classes.gridItemLeft}>
              <Typography variant='body2'>
                {formatDate(page.updatedAt)}
              </Typography>
            </Grid>

            <Grid item md={1.5} className={classes.gridItemLeft}>
              <Chip
                label={page.isPublished ? 'Published' : 'Draft'}
                color={page.isPublished ? 'success' : 'primary'}
                size='small'
                className={page.isPublished ? classes.publishedChip : classes.draftChip}
              />
            </Grid>

            <Grid item md={2} className={classes.gridItemRight}>
              <Box className={classes.listActions}>
                <ActionButton
                  variant='secondary'
                  size='small'
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onPageEdit(page)
                  }}
                  dataTest={`edit-page-${index}`}
                >
                Edit
                </ActionButton>

                {!page.canDelete === false && (
                  <IconButton
                    size='small'
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleDeleteClick(page)
                    }}
                    className={classes.deleteButton}
                    data-test={`delete-page-${index}`}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            </Grid>
          </Grid>
        ))}

        {(pages.length === 0 && !isLoading) && (
          <Grid item xs={12} container padding={2}>
            <Typography variant='body2' color='textSecondary' align='center'>
              No pages found
            </Typography>
          </Grid>
        )}
      </Paper>

      <CreatePageModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreatePage={handleCreatePage}
      />

      <ConfirmationAlert
        title='Delete page?'
        description={`Are you sure you want to delete "${pageToDelete?.name}"? This action cannot be undone.`}
        agreeText='Delete Page'
        showModal={deleteModalOpen}
        onConfirmation={handleConfirmDelete}
        onCancel={handleCancelDelete}
        disableBackdropClick
        data-test='delete-page-confirmation-modal'
      />
    </Box>
  )
}

export default PagesList
